import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { CheckCircle2 } from "lucide-react";

export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("all");

  const load = () => api.get("/tickets").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const closeTicket = async (id) => {
    try {
      await api.put(`/tickets/${id}/close`);
      toast.success("Ticket closed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = (rows || []).filter((t) =>
    tab === "all" ? true : tab === "active" ? t.status === "Active" : t.status === "Closed"
  );
  const canClose = user?.role === "technician" || user?.role === "admin";

  return (
    <div>
      <h1 className="text-4xl mb-1">Tickets</h1>
      <p className="text-muted-foreground mb-6">Auto-generated on every converted sale and assigned to a technician.</p>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="rounded-none bg-secondary">
          <TabsTrigger value="all" data-testid="tab-all" className="rounded-none">All</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active" className="rounded-none">Active</TabsTrigger>
          <TabsTrigger value="closed" data-testid="tab-closed" className="rounded-none">Closed</TabsTrigger>
        </TabsList>
      </Tabs>

      {rows === null ? (
        <p className="font-mono text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-border bg-card p-10 text-center text-muted-foreground">No tickets found.</div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary hover:bg-secondary">
                <TableHead className="font-mono text-xs uppercase">Ticket ID</TableHead>
                <TableHead className="font-mono text-xs uppercase">Customer</TableHead>
                <TableHead className="font-mono text-xs uppercase">Description</TableHead>
                <TableHead className="font-mono text-xs uppercase">Assigned To</TableHead>
                <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                <TableHead className="font-mono text-xs uppercase">Closed</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((t) => (
                <TableRow key={t.id} data-testid={`ticket-row-${t.ticket_id}`}>
                  <TableCell className="font-mono text-primary">{t.ticket_id}</TableCell>
                  <TableCell>
                    <button
                      className="font-medium hover:underline text-left"
                      onClick={() => navigate(`/customers/${t.customer_ref}`)}
                    >
                      {t.customer_name} <span className="font-mono text-xs text-muted-foreground">({t.customer_id})</span>
                    </button>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{t.description}</TableCell>
                  <TableCell className="font-mono text-sm">{t.assigned_to_name}</TableCell>
                  <TableCell><StatusBadge value={t.status} /></TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {t.closed_at ? new Date(t.closed_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    {t.status === "Active" && canClose && (
                      <Button size="sm" data-testid={`close-${t.ticket_id}`} className="rounded-none" onClick={() => closeTicket(t.id)}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Close
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
